import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, type Me } from './api.js';

interface AuthState {
  me: Me | null;
  isLoading: boolean;
  isUnauthorized: boolean;
  error: Error | null;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.isUnauthorized()) return false;
      return failureCount < 1;
    },
    staleTime: 5 * 60_000,
  });
  const value: AuthState = {
    me: q.data ?? null,
    isLoading: q.isLoading,
    isUnauthorized: q.error instanceof ApiError && q.error.isUnauthorized(),
    error: q.error ?? null,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth used outside AuthProvider');
  return v;
}
